import React from 'react';
import { setIsOpen } from '../../redux/slices/chatbotSlice';
import { useChatbotLogic } from './useChatbotLogic';
import { useGetChatbotSettingsQuery } from '../../redux/api/chatbotApi';
import { Tooltip } from 'antd';
import { checkIsSubscribed } from '../../utils/functions';
import { role as roleKey } from '../../utils/data';

const Chatbot: React.FC = () => {
  const {
    dispatch,
    isOpen,
    isLoading,
    isError,
    messages,
    isTyping,
    messagesEndRef,
    chatbotRef,
    inputValue,
    setInputValue,
    handleSendMessage,
    handleOptionClick,
    currentUser,
    getUserInitials
  } = useChatbotLogic();

  const { data: chatbotSettings } = useGetChatbotSettingsQuery();
  const settingsData = chatbotSettings ? (Array.isArray(chatbotSettings) ? chatbotSettings[0] : chatbotSettings) : null;
  const botName = settingsData?.name || 'Chatbot';

  const userRole = localStorage.getItem(roleKey) || '';
  const isAdmin = userRole.toLowerCase() === 'admin';
  const isSubscribed = checkIsSubscribed(null, null, isAdmin);
  const isChatbotDisabled = !isAdmin && !isSubscribed;

  const chatbotBubble = (
      <div
        className={`w-[50px] h-[50px] md:w-[60px] md:h-[60px] bg-gradient-to-br from-[#fff] to-[#fff] rounded-full flex items-center justify-center shadow-[0_8px_24px_rgba(67,97,238,0.4)] transition-all duration-300 ${isChatbotDisabled ? 'opacity-95 saturate-30 cursor-not-allowed' : 'cursor-pointer hover:scale-110 active:scale-95'} ${isOpen ? 'rotate-90' : ''}`}
        onClick={() => {
          if (!isChatbotDisabled) {
            dispatch(setIsOpen(!isOpen));
          }
        }}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        ) : (
          <svg style={{ width: '60%', height: '60%' }} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="8" fill="#0B1D3A" />
            <rect x="48" y="10" width="4" height="25" fill="#59B359" />
            <rect x="48" y="65" width="4" height="25" fill="#59B359" />
            <rect x="10" y="48" width="25" height="4" fill="#2C6DF2" />
            <rect x="65" y="48" width="25" height="4" fill="#59B359" />
            <rect x="22" y="22" width="4" height="25" transform="rotate(-45 24 24)" fill="#0B1D3A" />
            <rect x="74" y="22" width="4" height="25" transform="rotate(45 76 24)" fill="#0B1D3A" />
            <rect x="22" y="74" width="4" height="25" transform="rotate(45 24 76)" fill="#0B1D3A" />
            <rect x="74" y="74" width="4" height="25" transform="rotate(-45 76 76)" fill="#0B1D3A" />
          </svg>
        )}
      </div>
  );

  return (
    <div
      ref={chatbotRef}
      className="fixed bottom-[15px] right-[15px] md:bottom-[30px] md:right-[30px] z-[9999] font-['Lexend','Inter',sans-serif] select-none"
    >
      {isChatbotDisabled ? (
        <Tooltip title="An active subscription is required to use this feature." placement="topLeft" overlayInnerStyle={{ backgroundColor: '#1f2937', color: 'white', borderRadius: '4px' }}>
          {chatbotBubble}
        </Tooltip>
      ) : (
        chatbotBubble
      )}

      {isOpen && !isChatbotDisabled && (
        <div className="absolute bottom-[70px] md:bottom-[80px] right-0 w-[300px] sm:w-[350px] md:w-[400px] max-w-[calc(100vw-30px)] h-[450px] md:h-[580px] bg-white/95 backdrop-blur-xl border border-white/40 rounded-[20px] md:rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
          <div className="p-3.5 md:p-5 bg-[#1e293b] text-white flex justify-between items-center shadow-lg">
            <div className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full ${isLoading ? 'bg-yellow-400 animate-pulse' : isError ? 'bg-red-500' : 'bg-green-400 animate-pulse'}`}></div>
              <div className="flex flex-col">
                <h3 className="m-0 text-[16px] font-bold tracking-tight">{botName}</h3>
              </div>
            </div>
            <div className="p-1 hover:bg-white/10 rounded-full cursor-pointer transition-colors" onClick={() => dispatch(setIsOpen(false))}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4 select-text scroll-smooth">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-3 mb-2 ${msg.type === 'bot' ? 'flex-row' : 'flex-row-reverse'}`}>
                {msg.type === 'bot' ? (
                  <div className="w-8 h-8 p-[3px] rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-md border border-slate-200">
                    <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="50" cy="50" r="8" fill="#0B1D3A" />
                      <rect x="48" y="10" width="4" height="25" fill="#59B359" />
                      <rect x="48" y="65" width="4" height="25" fill="#59B359" />
                      <rect x="10" y="48" width="25" height="4" fill="#2C6DF2" />
                      <rect x="65" y="48" width="25" height="4" fill="#59B359" />
                      <rect x="22" y="22" width="4" height="25" transform="rotate(-45 24 24)" fill="#0B1D3A" />
                      <rect x="74" y="22" width="4" height="25" transform="rotate(45 76 24)" fill="#0B1D3A" />
                      <rect x="22" y="74" width="4" height="25" transform="rotate(45 24 76)" fill="#0B1D3A" />
                      <rect x="74" y="74" width="4" height="25" transform="rotate(-45 76 76)" fill="#0B1D3A" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-[#fbd38d] text-[#744210] flex items-center justify-center rounded-full font-semibold text-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-opacity border-2 border-white box-border flex-shrink-0">
                    {getUserInitials(currentUser.fullname)}
                  </div>
                )}

                <div className={`flex flex-col ${msg.type === 'bot' ? 'items-start' : 'items-end'} max-w-[80%]`}>
                  <div className={`p-3.5 px-4 rounded-[20px] text-[14px] leading-relaxed shadow-sm transition-all duration-300 ${msg.type === 'bot'
                    ? 'bg-white text-slate-800 rounded-tl-none border border-slate-100'
                    : 'bg-[#4361ee] text-white rounded-tr-none font-medium'
                    }`}>
                    {msg.text}
                  </div>

                  {msg.type === 'bot' && msg.options && (
                    <div className="mt-3.5 flex flex-col gap-3.5 w-full">
                      {/* 1. Help Topics / Categories section (highly compact, minimum lines) */}
                      {msg.options.some(opt => opt.type === 'source' && opt.variant !== 'back_source' && opt.variant !== 'back_question') && (
                        <div className="flex flex-col gap-1.5 w-full">
                          <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider font-sans">Help Topics</span>
                          <div className="flex flex-wrap gap-1.5 w-full">
                            {msg.options
                              .filter(opt => opt.type === 'source' && opt.variant !== 'back_source' && opt.variant !== 'back_question')
                              .map((opt, oIdx) => (
                                <button
                                  key={oIdx}
                                  onClick={() => handleOptionClick(opt)}
                                  className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 bg-white hover:border-[#4361ee] hover:bg-[#4361ee]/5 hover:text-[#4361ee] transition-all cursor-pointer shadow-sm active:scale-95 whitespace-nowrap"
                                >
                                  {opt.label}
                                </button>
                              ))
                            }
                          </div>
                        </div>
                      )}

                      {/* 2. Suggested Onboarding Questions section */}
                      {msg.options.some(opt => opt.type === 'faq' && opt.variant !== 'back_question' && opt.variant !== 'back_source') && (
                        <div className="flex flex-col gap-1.5 w-full">
                          <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider font-sans">Suggested Questions</span>
                          <div className="flex flex-col gap-1.5 w-full">
                            {msg.options
                              .filter(opt => opt.type === 'faq' && opt.variant !== 'back_question' && opt.variant !== 'back_source')
                              .map((opt, oIdx) => (
                                <button
                                  key={oIdx}
                                  onClick={() => handleOptionClick(opt)}
                                  className="px-3.5 py-2 border border-slate-200 rounded-xl text-[12px] font-semibold text-left text-slate-700 bg-white hover:border-[#4361ee] hover:bg-[#4361ee]/5 hover:text-[#4361ee] transition-all cursor-pointer shadow-sm active:scale-95 w-full"
                                >
                                  {opt.label}
                                </button>
                              ))
                            }
                          </div>
                        </div>
                      )}

                      {/* 3. Back buttons / Other actions */}
                      {msg.options.some(opt => opt.variant === 'back_source' || opt.variant === 'back_question') && (
                        <div className="flex flex-wrap gap-1.5 w-full">
                          {msg.options
                            .filter(opt => opt.variant === 'back_source' || opt.variant === 'back_question')
                            .map((opt, oIdx) => (
                              <button
                                key={oIdx}
                                onClick={() => handleOptionClick(opt)}
                                className="px-3.5 py-2 border border-blue-200 bg-blue-50 text-blue-600 rounded-xl text-[12px] font-semibold text-left hover:bg-blue-100 transition-all cursor-pointer shadow-sm active:scale-95"
                              >
                                {opt.label}
                              </button>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="self-start bg-white p-3 px-4 rounded-[18px] rounded-bl-none border border-slate-100 flex gap-1.5 items-center shadow-sm">
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></span>
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.4s]"></span>
              </div>
            )}
            <div ref={messagesEndRef} className="h-2" />
          </div>

          <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-slate-100 flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask a question..."
              className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[13px] text-slate-700 outline-none focus:border-[#4361ee] focus:bg-white transition-all font-medium font-sans"
            />
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className="px-4 py-2 bg-[#4361ee] hover:bg-[#3a0ca3] text-white rounded-xl text-[13px] font-semibold transition-all active:scale-95 shadow-md flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
            >
              Send
            </button>
          </form>

          <div className="p-3 bg-slate-50/50 border-t border-slate-100 text-center">
            <p className="text-[10px] text-slate-400 font-medium">Powered by EmployPro • Instant Support</p>
          </div>

        </div>
      )}
    </div>
  );
};

export default Chatbot;
